import { formatDateForApi, parseCivilDate } from '@/shared/lib';
import type { TrainingDetail } from '@/entities/Training';
import type { OnboardingMinistranteRow } from './useOnboardingMinistrantes';
import type { useTrainingFormFields } from './useTrainingFormFields';
import type { useTrainingFormResources } from './useTrainingFormResources';

type ScheduleAndTraineeFields = ReturnType<typeof useTrainingFormFields>;
type FormResources = ReturnType<typeof useTrainingFormResources>;

/**
 * Shared by Onboarding and Alteração de Função (the two "one scheduler +
 * one trainee" form types): pre-fills the schedule/trainee fields from a
 * TrainingDetail in edit mode, and the member->position auto-fill setters.
 * SGQ Pontual/Treinamento Aberto don't use this — they have participants,
 * not a single trainee.
 */
export function useScheduleAndTraineeEditing(
  fields: ScheduleAndTraineeFields,
  resources: FormResources,
  editContext?: { initial: TrainingDetail }
) {
  // The type-specific Form.vue also calls load() from its own onMounted — needed so the
  // create flow has a load trigger at all — but in the edit flow useEditTraining.ts's
  // dispatcher already awaited load() once before that component ever mounts. Without this
  // guard, that second call would reapply editContext.initial and wipe out a "runs only
  // once" default such as resources.load()'s null-revision fallback, flipping the
  // discard-changes guard on for a screen the user never touched.
  let editContextApplied = false;

  function applyInitial(initial: TrainingDetail): void {
    fields.title.value = initial.title;
    fields.notes.value = initial.notes ?? '';
    fields.scheduledByMemberId.value = initial.scheduling.responsible?.id ?? null;
    fields.scheduledByPositionId.value = initial.scheduling.position?.id ?? null;
    fields.scheduledDate.value = parseCivilDate(initial.scheduling.date);
    const trainee = initial.trainees[0];
    fields.traineeMemberId.value = trainee?.memberId ?? null;
    fields.traineePositionId.value = trainee?.position?.id ?? null;
    fields.trainingMatrixRevisionId.value = initial.matrixRevision?.id ?? null;
  }

  async function load(): Promise<void> {
    if (editContext && !editContextApplied) {
      editContextApplied = true;
      applyInitial(editContext.initial);
    }
    await resources.load(fields.trainingMatrixRevisionId);
  }

  function setScheduledByMemberId(memberId: number | null): void {
    fields.scheduledByMemberId.value = memberId;
    fields.scheduledByPositionId.value = resources.resolvePositionForMember(memberId);
  }

  function setTraineeMemberId(memberId: number | null): void {
    fields.traineeMemberId.value = memberId;
    fields.traineePositionId.value = resources.resolvePositionForMember(memberId);
  }

  return { applyInitial, load, setScheduledByMemberId, setTraineeMemberId };
}

/** The schedule/trainee/ministrantes portion shared by both types' dirty-tracking snapshot. */
export function buildScheduleSnapshotFields(
  fields: ScheduleAndTraineeFields,
  ministrantes: { rows: { value: OnboardingMinistranteRow[] } }
) {
  return {
    title: fields.title.value.trim(),
    notes: fields.notes.value.trim(),
    scheduledByMemberId: fields.scheduledByMemberId.value,
    scheduledByPositionId: fields.scheduledByPositionId.value,
    scheduledDate: fields.scheduledDate.value ? formatDateForApi(fields.scheduledDate.value) : null,
    traineeMemberId: fields.traineeMemberId.value,
    traineePositionId: fields.traineePositionId.value,
    trainingMatrixRevisionId: fields.trainingMatrixRevisionId.value,
    ministrantes: ministrantes.rows.value.map((row) => ({
      responsibleMemberId: row.responsibleMemberId,
      responsibleUserPositionId: row.responsibleUserPositionId,
      trainingDate: row.trainingDate ? formatDateForApi(row.trainingDate) : null,
    })),
  };
}
